const { migrateRoomsToMeters } = require('../constants.js');

/**
 * Composable for shared room management logic.
 * Used by RoomForm component on both product and category pages.
 *
 * @param {Object} options
 * @param {Function} [options.onRoomsChanged] - Called after room data changes (save, delete, primary change)
 * @param {Function} [options.onRoomDeleted] - Called when a room is deleted, receives { deletedIndex }
 */
function useRooms(options = {}) {
	const { ref, watch, nextTick } = Vue;

	const {
		onRoomsChanged = null,
		onRoomDeleted = null,
	} = options;

	// State refs
	const roomName = ref('');
	const width = ref('');
	const length = ref('');
	const directArea = ref('');
	const unitType = ref('m2');
	const rooms = ref([]);
	const inputMode = ref('dimensions');
	const createNewRoom = ref(false);
	const modifyRoom = ref(false);
	const primaryRoomIndex = ref(1);
	const confirmDelete = ref(false);
	const maxAreaMeters = ref(10000);
	const isLoggedIn = ref(window.fkCalculatorData?.isLoggedIn || false);
	const isSaving = ref(false);

	// Auto-show create form when rooms are empty
	watch(rooms, (newRooms) => {
		if (newRooms.length === 0) {
			createNewRoom.value = true;
		}
	}, { deep: true });

	const calculateProjectSize = () => {
		if (directArea.value && inputMode.value === 'total-area') {
			return directArea.value;
		}

		if (!width.value || !length.value) {
			return null;
		}

		return (width.value * length.value).toFixed(2);
	};

	const resetForm = () => {
		roomName.value = '';
		width.value = '';
		length.value = '';
		directArea.value = '';
		createNewRoom.value = false;
		modifyRoom.value = false;
	};

	const saveRoomData = () => {
		// Validate room name
		if (!roomName.value || roomName.value.length === 0) return false;

		// Validate numeric inputs based on input mode
		if (inputMode.value === 'dimensions') {
			if (!Number.isFinite(width.value) || width.value <= 0) return false;
			if (!Number.isFinite(length.value) || length.value <= 0) return false;
		}

		if (inputMode.value === 'total-area') {
			if (!Number.isFinite(directArea.value) || directArea.value <= 0) return false;
		}

		let projectSize = calculateProjectSize();

		if (projectSize > maxAreaMeters.value) {
			projectSize = maxAreaMeters.value;
		}

		const newRoom = {
			roomName: roomName.value,
			inputMode: inputMode.value,
			projectSize: projectSize,
			width: width.value,
			length: length.value,
			unitType: 'm2',
		};

		const existingRoomIndex = rooms.value.findIndex(
			(room) => room.roomName === roomName.value
		);

		if (existingRoomIndex !== -1 && createNewRoom.value) {
			return false;
		} else if (modifyRoom.value) {
			rooms.value[primaryRoomIndex.value - 1] = newRoom;
		} else {
			rooms.value.push(newRoom);
			setPrimaryRoom(rooms.value.length);
		}

		// Save rooms - to server for logged-in users, to cookie for guests
		if (isLoggedIn.value) {
			saveRoomsToServer();
		} else {
			Cookies.set('roomsData', JSON.stringify(rooms.value), { expires: 7 });
		}

		if (rooms.value.length === 1) {
			setPrimaryRoom(1);
		}

		onRoomsChanged?.();
		resetForm();
		return true;
	};

	const editRoom = () => {
		const existingRoomIndex = rooms.value.findIndex(
			(room) => room.roomName === rooms.value[primaryRoomIndex.value - 1].roomName
		);

		if (existingRoomIndex !== -1) {
			modifyRoom.value = true;
			roomName.value = rooms.value[existingRoomIndex].roomName;
			unitType.value = rooms.value[existingRoomIndex].unitType;
			inputMode.value = rooms.value[existingRoomIndex].inputMode;
			length.value = rooms.value[existingRoomIndex].length;
			width.value = rooms.value[existingRoomIndex].width;
			directArea.value = rooms.value[existingRoomIndex].projectSize;
		}
	};

	const deleteRoom = () => {
		if (!confirmDelete.value) {
			confirmDelete.value = true;
			return;
		} else {
			confirmDelete.value = false;
		}

		if (primaryRoomIndex.value > 0 && primaryRoomIndex.value <= rooms.value.length) {
			const deletedIndex = primaryRoomIndex.value;
			rooms.value.splice(primaryRoomIndex.value - 1, 1);
			primaryRoomIndex.value = 1;

			onRoomDeleted?.({ deletedIndex });
			onRoomsChanged?.();

			// Save rooms - to server for logged-in users, to cookie for guests
			if (isLoggedIn.value) {
				saveRoomsToServer();
			} else {
				Cookies.set('roomsData', JSON.stringify(rooms.value), { expires: 7 });
				Cookies.set('primaryRoomIndex', primaryRoomIndex.value, { expires: 7 });
			}
		}
	};

	const loadRooms = () => {
		if (isLoggedIn.value) {
			rooms.value = window.fkCalculatorData?.rooms || [];
			primaryRoomIndex.value = window.fkCalculatorData?.primaryRoomIndex || 1;

			if (rooms.value.length > 0 &&
				(isNaN(primaryRoomIndex.value) || primaryRoomIndex.value > rooms.value.length)) {
				primaryRoomIndex.value = 1;
			}
		} else {
			const roomsData = Cookies.get('roomsData');
			if (roomsData) {
				let parsedRooms;
				try {
					parsedRooms = JSON.parse(roomsData);
				} catch (e) {
					console.warn('Invalid roomsData cookie; clearing.', e);
					Cookies.remove('roomsData');
					return;
				}

				const { rooms: migratedRooms, migrated } = migrateRoomsToMeters(parsedRooms);
				rooms.value = migratedRooms;

				if (migrated) {
					Cookies.set('roomsData', JSON.stringify(migratedRooms), { expires: 7 });
				}

				const primaryRoomIndexCookie = Cookies.get('primaryRoomIndex');
				if (primaryRoomIndexCookie !== null && primaryRoomIndexCookie !== '') {
					primaryRoomIndex.value = parseInt(primaryRoomIndexCookie);
				}

				if (rooms.value.length > 0 &&
					(isNaN(primaryRoomIndex.value) ||
					primaryRoomIndex.value > rooms.value.length)) {
					primaryRoomIndex.value = 1;
					setPrimaryRoomLocal(1);
				}
			}
		}
	};

	const saveRoomsToServer = async () => {
		if (!isLoggedIn.value || isSaving.value) return;

		isSaving.value = true;
		try {
			const formData = new FormData();
			formData.append('action', 'fk_save_rooms');
			formData.append('nonce', window.fkCalculatorData.nonce);
			formData.append('rooms', JSON.stringify(rooms.value));
			formData.append('primaryIndex', primaryRoomIndex.value);

			const response = await fetch(window.fkCalculatorData.ajaxUrl, {
				method: 'POST',
				body: formData
			});
			if (!response.ok) {
				throw new Error('Failed to save rooms (' + response.status + ')');
			}
		} catch (error) {
			console.error('Error saving rooms to server:', error);
		} finally {
			isSaving.value = false;
		}
	};

	const setPrimaryRoomOnServer = async (index) => {
		if (!isLoggedIn.value) return;

		try {
			const formData = new FormData();
			formData.append('action', 'fk_set_primary_room');
			formData.append('nonce', window.fkCalculatorData.nonce);
			formData.append('primaryIndex', index);

			const response = await fetch(window.fkCalculatorData.ajaxUrl, {
				method: 'POST',
				body: formData
			});
			if (!response.ok) {
				throw new Error('Failed to set primary room (' + response.status + ')');
			}
		} catch (error) {
			console.error('Error setting primary room on server:', error);
		}
	};

	const setPrimaryRoomLocal = (index) => {
		primaryRoomIndex.value = parseInt(index);
		Cookies.set('primaryRoomIndex', primaryRoomIndex.value, { expires: 7 });
	};

	const openAuthModal = (mode) => {
		window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { mode } }));
	};

	const setPrimaryRoom = (index) => {
		primaryRoomIndex.value = parseInt(index);

		if (isLoggedIn.value) {
			setPrimaryRoomOnServer(index);
		} else {
			Cookies.set('primaryRoomIndex', primaryRoomIndex.value, { expires: 7 });
		}
	};

	const toggleInputMode = () => {
		inputMode.value = inputMode.value === 'dimensions' ? 'total-area' : 'dimensions';

		if (directArea.value != calculateProjectSize()) {
			nextTick(() => {
				width.value = '';
				length.value = '';
			});
		}
	};

	return {
		// State
		roomName,
		width,
		length,
		directArea,
		unitType,
		rooms,
		inputMode,
		createNewRoom,
		modifyRoom,
		primaryRoomIndex,
		confirmDelete,
		maxAreaMeters,
		isLoggedIn,
		isSaving,
		// Methods
		calculateProjectSize,
		loadRooms,
		saveRoomData,
		editRoom,
		deleteRoom,
		resetForm,
		setPrimaryRoom,
		toggleInputMode,
		openAuthModal,
		saveRoomsToServer,
	};
}

module.exports = { useRooms };
